/**
 * Reset Password Page
 *
 * Handles the password reset flow after user clicks the email link.
 * Supabase detects the recovery token from the URL automatically.
 */

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword } from '../lib/supabase';

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await updatePassword(password);
      setIsSuccess(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update password';
      if (message.includes('expired') || message.includes('invalid')) {
        setError('This reset link has expired. Please request a new one.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 p-8">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Saqr Skills
          </span>
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-500">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Password updated</h2>
            <p className="text-sm text-slate-500 mb-8">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate('/')}
              className="h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30"
            >
              Go to Sign In
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              Set new password
            </h2>
            <p className="text-sm text-slate-500 mb-8">
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/20 outline-none transition-all"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/20 outline-none transition-all"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 rounded-full bg-sky-500 px-8 text-sm font-semibold text-white hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
